import { parseCalendarDay } from '@/shared/lib/dateTime';
import { dateTimeFilterToBounds } from '@/shared/lib/dateTimeFilter';
import {
    ApiManualFnsReceiptStatus,
    ApiOrderCancelType,
    ApiOrderReviewStatus,
    ApiOrderStatus,
    ApiOrderType,
    ApiPetPlacement,
    ApiPetType,
    ApiProductType,
    ApiSpecialistType,
    ApiWhoWillBringPet,
    OrderRequestSource,
    OrderSortField,
    type CreateOrderRequest,
    type CancelOrderRequest,
    type GetOrdersRequest,
    type OrderResponse,
    type UpdateOrderRequest,
} from '@/core/generated/api';
import { cleanPhone, formatDateToDateOnly } from '@/core/format';
import type { DataTableFilterMeta } from 'primereact/datatable';
import {
    asFiniteFilterNumber,
    asGuardedFilterArray,
    asNonEmptyFilterString,
    getDataTableFilterValue,
} from '@/shared/lib/dataTableFilters';
import type { OrderFormValues } from './model/schema';
import type { CancelOrderFormValues } from './model/cancelOrderSchema';

export interface OrderListParams {
    filters: DataTableFilterMeta;
    take: number;
    skip: number;
    sortField?: string;
    sortOrder?: number;
}

/** Расширяет запрос фильтром питомца до регенерации API-клиента */
type GetOrdersRequestWithPetNameSearch = GetOrdersRequest & {
    petNameSearch?: string | null;
};

const DEFAULT_ORDER_TYPES: ApiOrderType[] = [
    ApiOrderType.DogWalking,
    ApiOrderType.PetBoarding,
    ApiOrderType.PetSitting,
    ApiOrderType.ToSpecialist,
];

const DEFAULT_ORDER_STATUSES: ApiOrderStatus[] = [
    ApiOrderStatus.Assigned,
    ApiOrderStatus.Cancelled,
    ApiOrderStatus.Completed,
    ApiOrderStatus.ExecutorApproved,
    ApiOrderStatus.InProgress,
    ApiOrderStatus.RequestPending,
    ApiOrderStatus.Searchable,
    ApiOrderStatus.SentToSignDocuments,
];

const ORDER_SORT_FIELDS: Readonly<Record<string, OrderSortField>> = {
    created: OrderSortField.Created,
    formedAt: OrderSortField.FormedAt,
    completedAt: OrderSortField.CompletedAt,
    'orderStatus.name': OrderSortField.Status,
    'orderType.name': OrderSortField.OrderType,
    id: OrderSortField.Id,
    orderNumber: OrderSortField.OrderNumber,
    clientName: OrderSortField.ClientName,
    executorFio: OrderSortField.ExecutorFio,
    startOrderTime: OrderSortField.StartOrderTime,
};

/** Проверяет тип заказа */
const isOrderType = (value: unknown): value is ApiOrderType =>
    Object.values(ApiOrderType).some((type) => type === value);

/** Проверяет статус заказа */
const isOrderStatus = (value: unknown): value is ApiOrderStatus =>
    Object.values(ApiOrderStatus).some((status) => status === value);

/** Читает логическое условие поиска заказов */
const asBooleanFilter = (value: unknown): boolean | null => {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return null;
};

/** Подготавливает телефон для поиска заказов */
const asPhoneFilter = (value: unknown): string | null => {
    const phone = asNonEmptyFilterString(value);

    return phone
        ? cleanPhone(phone)
        : null;
};

/** Преобразует фильтры и настройки таблицы в запрос списка заказов */
export const orderTableFiltersToListRequest = (
    {
        filters,
        take,
        skip,
        sortField,
        sortOrder,
    }: OrderListParams
): GetOrdersRequestWithPetNameSearch => {
    const receipt = getDataTableFilterValue(filters, 'manualFnsReceipt');

    const receiptStatus = Object.values(ApiManualFnsReceiptStatus)
        .find((status) => status !== ApiManualFnsReceiptStatus.Undefined && status === receipt);

    const review = asBooleanFilter(getDataTableFilterValue(filters, 'hasReview'));
    const selectedTypes = asGuardedFilterArray(getDataTableFilterValue(filters, 'orderType.id'), isOrderType);
    const selectedStatuses = asGuardedFilterArray(getDataTableFilterValue(filters, 'orderStatus.id'), isOrderStatus);

    const createdRange = dateTimeFilterToBounds(getDataTableFilterValue(filters, 'created'));
    const startOrderTimeRange = dateTimeFilterToBounds(getDataTableFilterValue(filters, 'startOrderTime'));
    const formedAtRange = dateTimeFilterToBounds(getDataTableFilterValue(filters, 'formedAt'));
    const completedAtRange = dateTimeFilterToBounds(getDataTableFilterValue(filters, 'completedAt'));

    return {
        createdFrom: createdRange?.from,
        createdTo: createdRange?.to,
        startOrderTimeFrom: startOrderTimeRange?.from,
        startOrderTimeTo: startOrderTimeRange?.to,
        formedAtFrom: formedAtRange?.from,
        formedAtTo: formedAtRange?.to,
        completedAtFrom: completedAtRange?.from,
        completedAtTo: completedAtRange?.to,
        hasManualFnsReceipt: receipt === 'none'
            ? false
            : undefined,
        manualFnsReceiptStatus: receiptStatus,
        takeCount: take,
        skipCount: skip,
        source: OrderRequestSource.Administration,
        orderNumberSearch: asNonEmptyFilterString(getDataTableFilterValue(filters, 'orderNumber')) ?? null,
        clientNameSearch: asNonEmptyFilterString(getDataTableFilterValue(filters, 'clientName')) ?? null,
        petNameSearch: asNonEmptyFilterString(getDataTableFilterValue(filters, 'petName')) ?? null,
        executorFioSearch: asNonEmptyFilterString(getDataTableFilterValue(filters, 'executorFio')) ?? null,
        clientPhoneSearch: asPhoneFilter(getDataTableFilterValue(filters, 'clientPhone')),
        executorPhoneSearch: asPhoneFilter(getDataTableFilterValue(filters, 'executorPhone')),
        cityId: asFiniteFilterNumber(getDataTableFilterValue(filters, 'address.cityId')) ?? null,
        sortBy: sortOrder === 0
            ? OrderSortField.FormedAt
            : ORDER_SORT_FIELDS[sortField ?? 'formedAt'] ?? OrderSortField.FormedAt,
        ascending: sortOrder === 1,
        isPaymentSentToExecutor: asBooleanFilter(getDataTableFilterValue(filters, 'isPaymentSentToExecutor')),
        isExecutorSignedAcceptanceAct: asBooleanFilter(getDataTableFilterValue(filters, 'isExecutorSignedAcceptanceAct')),
        orderReviewStatus: review === true
            ? ApiOrderReviewStatus.WithReview
            : review === false
                ? ApiOrderReviewStatus.WithoutReview
                : ApiOrderReviewStatus.All,
        orderTypes: selectedTypes.length > 0
            ? selectedTypes
            : [...DEFAULT_ORDER_TYPES],
        statuses: selectedStatuses.length > 0
            ? selectedStatuses
            : [...DEFAULT_ORDER_STATUSES],
    };
};

/** Преобразует загруженный заказ в значения формы */
export const orderResponseToFormValues = (order: OrderResponse): OrderFormValues => {
    const schedule = order.orderSchedulePeriods?.[0];
    const address = order.orderAddress;
    const specialistAddress = order.specialistAddress;

    return {
        clientId: order.clientId ?? '',
        clientAddressId: address?.id ?? '',
        clientAddressText: address
            ? `${address.regionName ?? ''}, ${address.cityName ?? ''}, ${address.street ?? ''}, ${address.houseNumber ?? ''}`
            : '',
        petType: order.petType?.id ?? '',
        productType: order.orderType?.id ?? '',
        clientSubscriptionId: order.orderProducts
            ?.find((product) => product.isBase && product.isSubscriptionProduct)
            ?.clientSubscriptionId ?? '',
        petIds: order.orderPets?.map((pet) => pet.petId).filter((id): id is string => Boolean(id)) ?? [],
        additionalProductIds: order.orderProducts
            ?.filter((product) => product.productType?.id === ApiProductType.AdditionalService)
            .map((product) => product.productId)
            .filter((id): id is string => Boolean(id)) ?? [],
        startDate: schedule?.startDate
            ? parseCalendarDay(schedule.startDate)
            : null,
        endDate: order.orderType?.id === ApiProductType.PetSitting
            ? parseCalendarDay(schedule?.startDate ?? '')
            : schedule?.endDate
                ? parseCalendarDay(schedule.endDate)
                : null,
        time: schedule?.localTime?.split('T')[1]?.slice(0, 5) ?? '',
        endTime: schedule?.localEndTime?.split('T')[1]?.slice(0, 5) ?? '',
        specialistAddressId: specialistAddress?.id ?? '',
        specialistAddressText: specialistAddress
            ? `${specialistAddress.regionName ?? ''}, ${specialistAddress.cityName ?? ''}, ${specialistAddress.street ?? ''}, ${specialistAddress.houseNumber ?? ''}`
            : '',
        specialistType: order.orderDetails?.specialistType?.id ?? '',
        petPlacement: order.orderDetails?.petPlacement?.id ?? '',
        whoWillBringPet: order.orderDetails?.whoWillBringPet?.id ?? '',
        isActiveCommand: order.orderDetails?.isActiveCommand ?? false,
        commandsDescription: order.orderDetails?.commandsDescription ?? '',
        comments: order.orderDetails?.comments ?? '',
        description: order.orderDetails?.description ?? '',
    };
};

/** Формирует общие поля запроса заказа из проверенной формы */
const orderFormValuesToBaseRequest = (values: OrderFormValues): CreateOrderRequest => ({
    clientId: values.clientId,
    clientSubscriptionId: values.clientSubscriptionId || null,
    petType: values.petType as ApiPetType,
    productType: values.productType as ApiProductType,
    clientAddressId: values.clientAddressId,
    petIds: values.petIds,
    additionalProductIds: values.additionalProductIds,
    specialistAddressId: values.specialistAddressId || null,
    specialistType: values.specialistType
        ? values.specialistType as ApiSpecialistType
        : undefined,
    petPlacement: values.petPlacement
        ? values.petPlacement as ApiPetPlacement
        : undefined,
    whoWillBringPet: values.whoWillBringPet
        ? values.whoWillBringPet as ApiWhoWillBringPet
        : undefined,
    isActiveCommand: values.isActiveCommand,
    commandsDescription: values.commandsDescription,
    comments: values.comments,
    description: values.description,
    schedulePeriod: {
        startDate: formatDateToDateOnly(values.startDate),
        endDate: formatDateToDateOnly(values.endDate),
        time: values.startDate
            ? `${formatDateToDateOnly(values.startDate)}T${values.time}:00`
            : null,
        endTime: (values.productType === ApiProductType.PetBoarding
            || values.productType === ApiProductType.PetSitting) && values.endDate && values.endTime
            ? `${formatDateToDateOnly(values.endDate)}T${values.endTime}:00`
            : null,
    },
});

/** Формирует запрос создания заказа с выбранным способом оплаты */
export const orderFormValuesToCreateRequest = (
    values: OrderFormValues,
): CreateOrderRequest => ({
    ...orderFormValuesToBaseRequest(values),
});

/** Формирует запрос обновления заказа из проверенной формы */
export const orderFormValuesToUpdateRequest = (
    values: OrderFormValues,
    orderId: string,
): UpdateOrderRequest => ({
    ...orderFormValuesToBaseRequest(values),
    orderId,
});

/** Заполняет форму отмены данными выбранного заказа */
export const orderResponseToCancelFormValues = (order: OrderResponse): CancelOrderFormValues => ({
    orderId: order.id ?? '',
    clientId: order.clientId ?? null,
    executorId: order.executorId ?? null,
    initiator: null,
    cancelType: null,
    description: '',
    hasPenalty: false,
    comment: '',
});

/** Формирует запрос административной отмены заказа */
export const cancelOrderFormValuesToRequest = (
    values: CancelOrderFormValues
): CancelOrderRequest => ({
    orderId: values.orderId,
    cancelType: values.cancelType ?? undefined,
    clientId: values.initiator === 'client'
        ? values.clientId
        : null,
    executorId: values.initiator === 'executor'
        ? values.executorId
        : null,
    description: values.cancelType === ApiOrderCancelType.Custom
        ? values.description.trim()
        : null,
    hasPenalty: values.initiator === 'executor' && values.hasPenalty,
    comment: values.comment.trim() || null,
    isAdminCancel: true,
});
